import type {
  BazelBuild,
  BazelInvocationMeta,
} from "@/client/director/buildLogs.tsx";
import {
  cacheStats,
  hasStatus,
  isTerminalStatus,
  runnerTone,
  targetOf,
} from "@/lib/bazel";

// The insights cover a recent window, computed in the browser from each
// build's invocation summary.
export const INSIGHTS_WINDOW_DAYS = 7;

type Totals = {
  // Cache hits over cacheable actions, the same rate a build's detail shows.
  cacheHits: number;
  cacheable: number;
  medianDurationMs?: number;
};

export type BuildSummary = Totals & {
  builds: number;
  succeeded: number;
  finished: number;
  remoteActions: number;
};

export type TrendPoint = {
  id: string;
  label: string;
  startedAt: string;
  status: string;
  failed: boolean;
  durationS: number;
  // Seconds split by where the build's actions ran, so a bar's colors show
  // its cache share while its height stays the build's duration.
  cachedS: number;
  remoteS: number;
  otherS: number;
};

export type TargetRow = Totals & {
  target: string;
  builds: number;
  lastStatus: string;
  lastStartedAt?: string;
};

function median(values: number[]): number | undefined {
  if (values.length === 0) return undefined;
  const sorted = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[mid]
    : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

// runnerCounts splits a build's actions by where they ran.
function runnerCounts(meta: BazelInvocationMeta | undefined): {
  hit: number;
  remote: number;
  other: number;
} {
  const counts = { hit: 0, remote: 0, other: 0 };
  for (const r of meta?.actions?.runners ?? []) {
    const tone = runnerTone(r.name);
    counts[tone === "hit" || tone === "remote" ? tone : "other"] += r.count;
  }
  return counts;
}

// totals sums the cache rate and the median duration over finished builds.
function totals(
  finished: BazelBuild[],
  metas: Map<string, BazelInvocationMeta>,
): Totals {
  let cacheHits = 0;
  let cacheable = 0;
  for (const b of finished) {
    const actions = metas.get(b.id)?.actions;
    if (!actions) continue;
    const c = cacheStats(actions, b.status);
    cacheHits += c.hits;
    cacheable += Math.max(c.cacheable, 0);
  }
  return {
    cacheHits,
    cacheable,
    medianDurationMs: median(
      finished.flatMap((b) => (b.durationMs != null ? [b.durationMs] : [])),
    ),
  };
}

const finishedOf = (builds: BazelBuild[]) =>
  builds.filter((b) => isTerminalStatus(b.status));

// inWindow keeps the builds that started within the window, newest first.
export function inWindow(
  builds: BazelBuild[],
  now: number,
  days = INSIGHTS_WINDOW_DAYS,
): BazelBuild[] {
  const since = now - days * 24 * 3600 * 1000;
  return builds.filter(
    (b) => b.startedAt && new Date(b.startedAt).getTime() >= since,
  );
}

export function summarize(
  builds: BazelBuild[],
  metas: Map<string, BazelInvocationMeta>,
): BuildSummary {
  const finished = finishedOf(builds);
  return {
    ...totals(finished, metas),
    builds: builds.length,
    finished: finished.length,
    succeeded: finished.filter((b) => hasStatus(b.status, "SUCCEEDED")).length,
    remoteActions: finished.reduce(
      (sum, b) => sum + runnerCounts(metas.get(b.id)).remote,
      0,
    ),
  };
}

// trend returns finished builds oldest first, each split by its action mix.
export function trend(
  builds: BazelBuild[],
  metas: Map<string, BazelInvocationMeta>,
): TrendPoint[] {
  return finishedOf(builds)
    .filter(
      (b): b is BazelBuild & { durationMs: number; startedAt: string } =>
        b.durationMs != null && !!b.startedAt,
    )
    .reverse()
    .map((b) => {
      const durationS = b.durationMs / 1000;
      const { hit, remote, other } = runnerCounts(metas.get(b.id));
      const total = hit + remote + other;
      const share = (n: number) => (total ? (n / total) * durationS : 0);
      return {
        id: b.id,
        label: targetOf(b),
        startedAt: b.startedAt,
        status: b.status,
        failed: hasStatus(b.status, "FAILED"),
        durationS,
        cachedS: share(hit),
        remoteS: share(remote),
        // A build without a summary shows as one neutral bar.
        otherS: total ? share(other) : durationS,
      };
    });
}

// targets groups the window's builds by target, most built first.
export function targets(
  builds: BazelBuild[],
  metas: Map<string, BazelInvocationMeta>,
): TargetRow[] {
  const groups = new Map<string, BazelBuild[]>();
  for (const b of builds) {
    const key = targetOf(b);
    const group = groups.get(key);
    if (group) group.push(b);
    else groups.set(key, [b]);
  }
  return [...groups.entries()]
    .map(([target, group]) => ({
      ...totals(finishedOf(group), metas),
      target,
      builds: group.length,
      // builds arrive newest first, so the group's first build is its latest.
      lastStatus: group[0]!.status,
      lastStartedAt: group[0]!.startedAt,
    }))
    .sort((a, b) => b.builds - a.builds || a.target.localeCompare(b.target));
}
